import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fundAPI } from '../services/api';
import { ArrowLeft, Plus, Trash2, Edit, TrendingUp, Loader2, ExternalLink, FileSpreadsheet, FileText, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import FundCustomFieldsTab from '../components/fund/FundCustomFieldsTab';
import FundInvestorsTab from '../components/fund/FundInvestorsTab';
import FundNAVTab from '../components/fund/FundNAVTab';
import { EntityAuditHistory } from './AuditTrail';
import FundDocumentTemplates from '../components/fund/FundDocumentTemplates';

const FundDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canDelete = false, canEdit = false, canCreate = false } = usePermissions() || {};
  const [fund, setFund] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [newClass, setNewClass] = useState({
    name: '',
    isin: '',
    description: '',
    face_value: '',
  });

  const showInvestors = user?.module_permissions?.investors === true;
  const showInvestments = user?.module_permissions?.investments === true;
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const tabs = [
    { id: 'overview', label: 'Overview & Classes' },
    ...(showInvestments ? [{ id: 'investments', label: 'Investments' }] : []),
    ...(showInvestors ? [{ id: 'investors', label: 'Investors' }] : []),
    { id: 'nav', label: 'NAV' },
    { id: 'templates', label: 'Document Templates' },
    { id: 'customfields', label: 'Custom Fields' },
    ...(isAdmin ? [{ id: 'history', label: 'History' }] : []),
  ];

  // Investment summary state
  const [investmentsSummary, setInvestmentsSummary] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const [invAsOnDate, setInvAsOnDate] = useState('');
  const [invStageFilter, setInvStageFilter] = useState('');
  const [exportingInv, setExportingInv] = useState(null);

  useEffect(() => {
    fetchFundDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'investments') {
      loadInvestmentsSummary();
    }
  }, [activeTab, invAsOnDate, invStageFilter]);

  const loadInvestmentsSummary = async () => {
    setLoadingInvestments(true);
    try {
      const res = await fundAPI.getInvestmentsSummary(id, {
        as_on_date: invAsOnDate || undefined,
        stage: invStageFilter || undefined,
      });
      setInvestmentsSummary(res.data);
    } catch {
      setInvestmentsSummary([]);
    } finally {
      setLoadingInvestments(false);
    }
  };

  const handleExportInvestments = async (format) => {
    setExportingInv(format);
    try {
      const res = await fundAPI.exportInvestmentsSummary(id, format, invAsOnDate || undefined, invStageFilter || undefined);
      const blob = new Blob([res.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(fund?.name || 'Fund').replace(/\s+/g, '_')}_Investments_${invAsOnDate || 'all'}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Export failed');
    } finally {
      setExportingInv(null);
    }
  };

  const fetchFundDetails = async () => {
    try {
      const [fundRes, classesRes] = await Promise.all([
        fundAPI.getById(id),
        fundAPI.getClasses(id),
      ]);
      setFund(fundRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      console.error('Failed to fetch fund details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        const res = await fundAPI.updateClass(id, editingClass.id, newClass);
        setClasses(classes.map(c => c.id === editingClass.id ? res.data : c));
        setEditingClass(null);
      } else {
        const response = await fundAPI.createClass(id, newClass);
        setClasses([...classes, response.data]);
      }
      setNewClass({ name: '', isin: '', description: '', face_value: '' });
      setShowClassForm(false);
    } catch (error) {
      alert('Failed to save class');
    }
  };

  const startEditClass = (cls) => {
    setEditingClass(cls);
    setNewClass({ name: cls.name, isin: cls.isin || '', description: cls.description || '', face_value: cls.face_value });
    setShowClassForm(true);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await fundAPI.deleteClass(id, classId);
      setClasses(classes.filter((c) => c.id !== classId));
    } catch (error) {
      alert('Failed to delete class');
    }
  };


  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!fund) {
    return <div className="text-center py-12">Fund not found</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/funds')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Funds
      </button>

      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="fund-name">
                {fund.name}
              </h1>
              <p className="text-gray-600 mt-2">
                Currency: {fund.fund_currency} | {fund.sebi_aif_category || 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(user?.module_permissions?.compliance === true) && (
                <Link
                  to={`/funds/${id}/compliance`}
                  className="flex items-center px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50"
                  data-testid="fund-compliance-btn"
                >
                  <ClipboardList size={18} className="mr-2" />
                  Compliance
                </Link>
              )}
              {canEdit && (
                <Link
                  to={`/funds/${id}/edit`}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit size={18} className="mr-2" />
                  Edit Fund
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors
                  ${activeTab === tab.id
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <>
              {/* Fund Details Grid */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Fund Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">Currency</p>
            <p className="text-lg font-semibold">{fund.fund_currency}</p>
          </div>

          {fund.target_size && (
            <div>
              <p className="text-sm text-gray-600">Target Size</p>
              <p className="text-lg font-semibold">
                {fund.fund_currency} {fund.target_size.toLocaleString(fund.fund_currency === 'INR' ? 'en-IN' : 'en-US')}
              </p>
            </div>
          )}

          {fund.actual_corpus && (
            <div>
              <p className="text-sm text-gray-600">Actual Corpus</p>
              <p className="text-lg font-semibold">
                {fund.fund_currency} {fund.actual_corpus.toLocaleString(fund.fund_currency === 'INR' ? 'en-IN' : 'en-US')}
              </p>
            </div>
          )}

          {fund.sebi_aif_category && (
            <div>
              <p className="text-sm text-gray-600">SEBI AIF Category</p>
              <p className="text-lg font-semibold">{fund.sebi_aif_category}</p>
            </div>
          )}

          {fund.sebi_registration_number && (
            <div>
              <p className="text-sm text-gray-600">SEBI Registration Number</p>
              <p className="text-lg font-semibold">{fund.sebi_registration_number}</p>
            </div>
          )}

          {fund.fund_suffix && (
            <div>
              <p className="text-sm text-gray-600">Fund Suffix</p>
              <p className="text-lg font-semibold" data-testid="fund-suffix-value">{fund.fund_suffix}</p>
            </div>
          )}

          {fund.management_fee_percentage && (
            <div>
              <p className="text-sm text-gray-600">Management Fee</p>
              <p className="text-lg font-semibold">{fund.management_fee_percentage}%</p>
            </div>
          )}
                </div>
              </div>

              {/* Unit Classes */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Unit Classes</h2>
                  {canEdit && (
                    <button
                      onClick={() => setShowClassForm(!showClassForm)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus size={18} className="mr-2" />
                      Add Class
                    </button>
                  )}
                </div>

                {showClassForm && (
          <form onSubmit={handleAddClass} className="mb-6 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{editingClass ? 'Edit Unit Class' : 'Add Unit Class'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
                <input type="text" required value={newClass.name}
                  onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ISIN</label>
                <input type="text" value={newClass.isin}
                  onChange={(e) => setNewClass({ ...newClass, isin: e.target.value.toUpperCase() })}
                  placeholder="e.g. INF123A01234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={newClass.description}
                  onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Face Value ({fund.fund_currency}) *</label>
                <input type="number" required step="0.01" value={newClass.face_value}
                  onChange={(e) => setNewClass({ ...newClass, face_value: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => { setShowClassForm(false); setEditingClass(null); setNewClass({ name: '', isin: '', description: '', face_value: '' }); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                {editingClass ? 'Update Class' : 'Add Class'}
              </button>
            </div>
                  </form>
                )}

                {classes.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No unit classes added yet</p>
                ) : (
                  <div className="space-y-3">
                    {classes.map((cls) => (
                      <div key={cls.id} className="flex justify-between items-start p-4 border border-gray-200 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{cls.name}</h4>
                            {cls.isin && (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-mono rounded">{cls.isin}</span>
                            )}
                          </div>
                          {cls.description && <p className="text-sm text-gray-600 mt-1">{cls.description}</p>}
                          <p className="text-sm text-gray-600 mt-1">Face Value: {fund.fund_currency} {cls.face_value}</p>
                        </div>
                        {canEdit && (
                          <div className="flex gap-2 ml-4">
                            <button onClick={() => startEditClass(cls)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-class-${cls.id}`}>
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDeleteClass(cls.id)} className="text-red-600 hover:text-red-700">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Custom Fields Tab */}
          {activeTab === 'customfields' && (
            <FundCustomFieldsTab
              fundId={id}
              fund={fund}
              onUpdate={fetchFundDetails}
            />
          )}

          {/* Investments Tab */}
          {activeTab === 'investments' && (
            <div data-testid="tab-content-investments">
              <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs font-medium text-gray-600">As on Date</label>
                  <input
                    type="date"
                    value={invAsOnDate}
                    onChange={e => setInvAsOnDate(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                    data-testid="inv-as-on-date"
                  />
                  {invAsOnDate && (
                    <button onClick={() => setInvAsOnDate('')} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-as-on-date">Clear</button>
                  )}
                  <span className="mx-1 text-gray-300">|</span>
                  <label className="text-xs font-medium text-gray-600">Stage</label>
                  <select
                    value={invStageFilter}
                    onChange={e => setInvStageFilter(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                    data-testid="inv-stage-filter"
                  >
                    <option value="">All Stages</option>
                    {Array.from(new Set(investmentsSummary.map(i => i.investment_stage).filter(Boolean)))
                      .concat(['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited'])
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {invStageFilter && (
                    <button onClick={() => setInvStageFilter('')} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="clear-stage-filter">Clear</button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportInvestments('excel')}
                    disabled={!investmentsSummary.length || exportingInv === 'excel'}
                    className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                    data-testid="export-inv-excel"
                  >
                    <FileSpreadsheet size={14} className="mr-1" /> {exportingInv === 'excel' ? 'Exporting...' : 'Excel'}
                  </button>
                  <button
                    onClick={() => handleExportInvestments('pdf')}
                    disabled={!investmentsSummary.length || exportingInv === 'pdf'}
                    className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                    data-testid="export-inv-pdf"
                  >
                    <FileText size={14} className="mr-1" /> {exportingInv === 'pdf' ? 'Exporting...' : 'PDF'}
                  </button>
                </div>
              </div>
              {loadingInvestments ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 size={20} className="animate-spin mr-2" /> Loading investments...
                </div>
              ) : investmentsSummary.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <TrendingUp size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No investments found for this fund</p>
                </div>
              ) : (
                <div className="bg-white border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                        <th className="px-4 py-3">Portfolio Company</th>
                        <th className="px-4 py-3">Stage</th>
                        <th className="px-4 py-3">Currency</th>
                        <th className="px-4 py-3 text-right">Total Invested</th>
                        <th className="px-4 py-3 text-right">Total Realisation</th>
                        <th className="px-4 py-3 text-right">MOIC</th>
                        <th className="px-4 py-3 text-right">IRR</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {investmentsSummary.map(inv => (
                        <tr
                          key={inv.id}
                          className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/investments/${inv.id}`)}
                          data-testid={`fund-investment-row-${inv.id}`}
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">{inv.portfolio_company_name}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              inv.investment_stage === 'Active' ? 'bg-green-100 text-green-700' :
                              inv.investment_stage === 'Exited' ? 'bg-gray-100 text-gray-600' :
                              inv.investment_stage === 'Written Off' ? 'bg-red-100 text-red-600' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {inv.investment_stage || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{inv.currency}</td>
                          <td className="px-4 py-3 text-right font-medium">{inv.total_invested.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right text-green-600">{inv.total_realisation.toLocaleString('en-IN')}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${inv.moic >= 1 ? 'text-green-600' : 'text-red-500'}`}>
                            {inv.moic > 0 ? `${inv.moic.toFixed(2)}x` : '-'}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${inv.irr >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {inv.irr !== 0 ? `${inv.irr.toFixed(1)}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ExternalLink size={14} className="text-gray-400" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold text-sm border-t">
                        <td className="px-4 py-3" colSpan={3}>Total ({investmentsSummary.length} investments)</td>
                        <td className="px-4 py-3 text-right">
                          {investmentsSummary.reduce((s, i) => s + i.total_invested, 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600">
                          {investmentsSummary.reduce((s, i) => s + i.total_realisation, 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3" colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Investors Tab */}
          {activeTab === 'investors' && (
            <FundInvestorsTab
              fundId={id}
              fundName={fund.name}
              fundCurrency={fund.fund_currency}
            />
          )}

          {/* Document Templates Tab */}
          {activeTab === 'templates' && (
            <FundDocumentTemplates fundId={id} canEdit={canEdit} />
          )}

          {/* NAV Tab */}
          {activeTab === 'nav' && (
            <FundNAVTab
              fundId={id}
              fundCurrency={fund.fund_currency}
              canEdit={canEdit}
              canCreate={canCreate}
              canDelete={canDelete}
            />
          )}

          {/* History Tab (Admin only) */}
          {activeTab === 'history' && isAdmin && (
            <div data-testid="tab-content-history">
              <EntityAuditHistory entityType="fund" entityId={id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundDetails;
